import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CatalogPlanSelector } from "./CatalogPlanSelector";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { getModuleDisplayName } from "../lib/modules";
import { mapRenewalPreferenceErrorCode } from "./renewal-preference-error-code-mapper";
import { formatCurrency } from "../utils/billingFormat";
import {
  getRenewalOptions,
  getRenewalTimeline,
  setRenewalPreference,
  clearRenewalPreference,
  RenewalPreferenceConflictError,
  RenewalPreferenceUnavailableError,
  type BillingInterval,
  type CatalogEntry,
  type RenewalOptionEntry,
  type RenewalTimelinePreview,
} from "../services/accountService";

export interface RenewalPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the preference has been saved (or reset) — parent should re-fetch the timeline preview. */
  onSaved: () => void;
  /** Modules of the currently active package, for the D-2 "Utracisz: {moduły}" comparison. */
  currentModules?: string[];
  /** D-1: an open unpaid renewal proforma — saving cancels and reissues it, so we confirm first. */
  pendingProforma?: { number: string; amountGross: number; currency: string } | null;
}

type PendingAction =
  | { type: "set"; productId: string; billingInterval: BillingInterval }
  | { type: "clear" };

type Step = "select" | "confirm-proforma";

/** AC-15: modal for choosing (or resetting) the package for the next billing period. */
export const RenewalPackageModal: React.FC<RenewalPackageModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  currentModules,
  pendingProforma = null,
}) => {
  const [options, setOptions] = useState<RenewalOptionEntry[] | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(false);
  // CRITICAL fix: 404 means the feature is flag-gated off, not a transient failure —
  // show a non-actionable message instead of a "Spróbuj ponownie" retry loop.
  const [optionsUnavailable, setOptionsUnavailable] = useState(false);
  // Best-effort, non-blocking: only used to derive the current-package badge and to
  // pre-select the effective renewal target. A failure here never blocks the modal.
  const [timelinePreview, setTimelinePreview] = useState<RenewalTimelinePreview | null>(null);
  // Review M-2: CatalogPlanSelector reads its initial selection on mount only, so it is
  // mounted once the preview has settled (loaded or failed) — otherwise a preview that
  // lands after the options would leave the default "monthly"/first card selected.
  const [timelineSettled, setTimelineSettled] = useState(false);
  const [selection, setSelection] = useState<{ productId: string; billingInterval: BillingInterval } | null>(null);
  const [step, setStep] = useState<Step>("select");
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadOptions = useCallback(() => {
    setOptionsLoading(true);
    setOptionsError(false);
    setOptionsUnavailable(false);
    getRenewalOptions()
      .then((res) => {
        setOptions(res.options);
        setOptionsLoading(false);
      })
      .catch((err) => {
        if (err instanceof RenewalPreferenceUnavailableError) {
          setOptionsUnavailable(true);
        } else {
          setOptionsError(true);
        }
        setOptionsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isOpen && !options && !optionsLoading) {
      loadOptions();
    }
    if (isOpen && !timelinePreview) {
      // Best-effort — a failure here only means no badge/pre-selection, never a blocking error.
      getRenewalTimeline()
        .then(setTimelinePreview)
        .catch(() => {})
        .finally(() => setTimelineSettled(true));
    }
    if (!isOpen) {
      setStep("select");
      setPendingAction(null);
      setErrorMessage(null);
      setSuccessMessage(null);
      setSelection(null);
      setTimelinePreview(null);
      setTimelineSettled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useFocusTrap(containerRef, isOpen);

  // Focus the dialog on open (DeleteTenantModal pattern) — content is dynamic
  // (spinner → options), so the container itself is the stable focus target.
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => containerRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, saving, onClose]);

  // AC-15: dedupe renewal-options rows (one per product × interval) into CatalogEntry cards.
  const catalogEntries: CatalogEntry[] = useMemo(() => {
    if (!options) return [];
    const byProduct = new Map<string, CatalogEntry>();
    options.forEach((opt, index) => {
      if (!opt.selectable) return;
      if (!byProduct.has(opt.productId)) {
        byProduct.set(opt.productId, {
          id: opt.productId,
          productId: opt.productId,
          displayName: opt.displayName,
          // No catalog metadata on purpose: CatalogPlanSelector would fall back to
          // `metadata.priceMonthly` for a cell without a price (AC-15).
          metadata: null,
          sortOrder: index,
        });
      }
    });
    return Array.from(byProduct.values());
  }, [options]);

  // Review M-1: modules per product, for the D-2 "Utracisz: {moduły}" comparison.
  const modulesByProduct = useMemo(() => {
    const byProduct = new Map<string, string[]>();
    options?.forEach((opt) => {
      if (!byProduct.has(opt.productId)) byProduct.set(opt.productId, opt.modules ?? []);
    });
    return byProduct;
  }, [options]);

  // Non-blocking: badge for the tenant's currently active package (MEDIUM #2).
  const currentProductId = timelinePreview?.currentProduct.id ?? null;
  // Review M-2: pre-select the effective renewal target (stored preference, own package,
  // upgrade or fallback alike) — never the selector's default "monthly"/first card.
  // CatalogPlanSelector falls back to the first card when the product isn't on the list.
  const initialSelectedProductId = timelinePreview?.renewalProduct.id ?? null;
  const initialSelectedInterval = timelinePreview?.billingInterval ?? null;

  const priceOverride = useCallback(
    (entry: CatalogEntry, billingInterval: BillingInterval) => {
      const match = options?.find(
        (o) => o.productId === entry.productId && o.billingInterval === billingInterval
      );
      return match?.price ?? null;
    },
    [options]
  );

  const lostModules = useMemo(() => {
    if (!selection || !currentModules || currentModules.length === 0) return [];
    const selectedModules = modulesByProduct.get(selection.productId) ?? [];
    // Unknown module list (catalog entry lists none) → make no "Utracisz" claim at all,
    // rather than reporting every current module as lost (review M-1).
    if (selectedModules.length === 0 || selectedModules.includes("grants:*")) return [];
    return currentModules.filter(
      (m) => m !== "grants:*" && !selectedModules.includes(m)
    );
  }, [selection, currentModules, modulesByProduct]);

  const doSave = useCallback(
    async (action: PendingAction) => {
      setSaving(true);
      setErrorMessage(null);
      try {
        const result =
          action.type === "set"
            ? await setRenewalPreference(action.productId, action.billingInterval)
            : await clearRenewalPreference();
        onSaved();
        if (result.proformaPending) {
          setSuccessMessage("Zmiana zapisana. Nową proformę wyślemy mailem w ciągu kilku minut.");
          setStep("select");
        } else {
          onClose();
        }
      } catch (err) {
        setErrorMessage(
          err instanceof RenewalPreferenceConflictError
            ? mapRenewalPreferenceErrorCode(err.errorCode)
            : "Nie udało się zapisać zmiany. Spróbuj ponownie."
        );
        setStep("select");
      } finally {
        setSaving(false);
        setPendingAction(null);
      }
    },
    [onSaved, onClose]
  );

  const handleCatalogSelect = (productId: string, billingInterval: BillingInterval) => {
    const action: PendingAction = { type: "set", productId, billingInterval };
    if (pendingProforma) {
      setPendingAction(action);
      setStep("confirm-proforma");
    } else {
      doSave(action);
    }
  };

  const handleStayCurrent = () => {
    const action: PendingAction = { type: "clear" };
    if (pendingProforma) {
      setPendingAction(action);
      setStep("confirm-proforma");
    } else {
      doSave(action);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
        data-testid="renewal-package-modal-backdrop"
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="renewal-package-modal-title"
          tabIndex={-1}
          className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all focus:outline-none"
          data-testid="renewal-package-modal"
        >
          <div className="border-b border-neutral-200 px-6 py-4">
            <h2 id="renewal-package-modal-title" className="text-lg font-semibold text-neutral-900">
              Zmień pakiet na kolejny okres
            </h2>
          </div>

          <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md" role="status">
                <p className="text-sm text-green-800">{successMessage}</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Zamknij
                </button>
              </div>
            )}

            {!successMessage && errorMessage && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {!successMessage && step === "confirm-proforma" && pendingProforma && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md space-y-3">
                <p className="text-sm text-amber-900">
                  Masz wystawioną proformę {pendingProforma.number} na{" "}
                  {formatCurrency(pendingProforma.amountGross, pendingProforma.currency)}. Zmiana
                  anuluje ją i wystawi nową — nie płać starej.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => pendingAction && doSave(pendingAction)}
                    disabled={saving}
                    className="px-4 py-2 text-sm bg-primary-700 text-white rounded hover:bg-primary-800 disabled:opacity-50"
                  >
                    {saving ? "Zapisywanie..." : "Zmień mimo to"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("select");
                      setPendingAction(null);
                    }}
                    disabled={saving}
                    className="px-4 py-2 text-sm border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            )}

            {!successMessage && step === "select" && (
              <>
                {(optionsLoading || (options && !timelineSettled)) && (
                  <div className="text-center py-8" aria-live="polite" aria-busy="true">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-3 text-gray-600">Ładowanie pakietów...</p>
                  </div>
                )}

                {!optionsLoading && optionsUnavailable && (
                  <div role="status" data-testid="renewal-package-modal-unavailable">
                    <p className="text-sm text-gray-600">
                      Zmiana pakietu na kolejny okres jest chwilowo niedostępna.
                    </p>
                  </div>
                )}

                {!optionsLoading && !optionsUnavailable && optionsError && (
                  <div role="alert">
                    <p className="text-red-700 text-sm mb-2">Nie udało się wczytać pakietów.</p>
                    <button
                      type="button"
                      onClick={loadOptions}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Spróbuj ponownie
                    </button>
                  </div>
                )}

                {!optionsLoading &&
                  !optionsError &&
                  !optionsUnavailable &&
                  options &&
                  timelineSettled && (
                  <>
                    <button
                      type="button"
                      onClick={handleStayCurrent}
                      disabled={saving}
                      className="text-sm font-medium text-primary-700 underline hover:text-primary-900 disabled:opacity-50"
                    >
                      Zostań przy obecnym pakiecie
                    </button>

                    <CatalogPlanSelector
                      entries={catalogEntries}
                      onSelect={handleCatalogSelect}
                      onSelectionChange={(productId, billingInterval) =>
                        setSelection({ productId, billingInterval })
                      }
                      priceOverride={priceOverride}
                      currentProductId={currentProductId}
                      initialSelectedProductId={initialSelectedProductId}
                      initialSelectedInterval={initialSelectedInterval}
                      loading={saving}
                      disabled={saving}
                      submitLabel="Zapisz wybór"
                      submitLoadingLabel="Zapisywanie..."
                    />

                    {lostModules.length > 0 && (
                      <div
                        data-testid="renewal-package-modal-lost-modules"
                        className="p-3 bg-blue-50 border border-blue-200 rounded-md"
                        role="note"
                      >
                        <p className="text-sm text-blue-800">
                          Utracisz: {lostModules.map((m) => getModuleDisplayName(m)).join(", ")}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalPackageModal;
